import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import AddIcon from '@mui/icons-material/Add'
import { IssueItem } from './IssueItem'
import { IssueFormDialog } from './IssueFormDialog'
import { AttachmentData } from './AttachmentItem'

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface IssueMessage {
  id: number
  message: string
  status: string
  agent: AgentRef
  attachments: AttachmentData[]
  created_at: string
}

export interface Issue {
  id: number
  message: string
  status: string
  issue_type: string
  assignee: AgentRef
  requestor: AgentRef
  messages: IssueMessage[]
  attachments: AttachmentData[]
  created_at: string
  updated_at: string
}

interface Props {
  issues: Issue[]
  issueType: 'clarification' | 'review'
  boardSlug: string
  taskId: number
  defaultAssigneeId: number
  agents: AgentRef[]
  onAddReview?: () => void
}

export function IssueList({ issues, issueType, boardSlug, taskId, defaultAssigneeId, agents, onAddReview }: Props) {
  const [showForm, setShowForm] = useState(false)
  const heading = issueType === 'clarification' ? 'Clarifications' : 'Review Items'
  const buttonLabel = issueType === 'clarification' ? 'Add Clarification' : 'Add Review Item'
  const emptyMessage = issueType === 'clarification' ? 'No clarifications yet.' : 'No review items yet.'

  const useComposer = issueType === 'review' && onAddReview

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h3" component="h2">
          {heading}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => useComposer ? onAddReview() : setShowForm(true)}
          sx={{ textTransform: 'none' }}
        >
          {buttonLabel}
        </Button>
      </Box>

      {issues.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {issues.map((issue) => (
            <IssueItem
              key={issue.id}
              issue={issue}
              boardSlug={boardSlug}
              taskId={taskId}
              issueType={issueType}
            />
          ))}
        </Box>
      )}

      {!useComposer && (
        <IssueFormDialog
          open={showForm}
          onClose={() => setShowForm(false)}
          boardSlug={boardSlug}
          taskId={taskId}
          defaultAssigneeId={defaultAssigneeId}
          agents={agents}
          issueType={issueType}
        />
      )}
    </Box>
  )
}
